import { defineStore } from 'pinia'
import beApiClient from "@/src/be-api-client";
import { APIRoute, EP } from "@/src/endpoints";
import { handleErrors, makeRequest } from "@/src/utils";
import { SuperTeamDashboardRow } from '@/src/models/dashboard/super_teams/super-team-dashboard-row';
import { AxiosError, AxiosResponse } from 'axios';
import { SaveStates } from "@/src/enums";
import { SuperTeamDashboard } from '@/src/models/dashboard/super_teams/super-team-dashboard';

type Lookup = {
  label: string;
  value: string;
}

const STATUS_LABELS: Record<string, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  verified: 'Verified'
}

type SuperTeamDashboardFilterParams = {
  super_team_dashboard_configuration_id?: string;
  match_code?: string;
  donor_code?: string;
  organ_group_ids?: any;
  transplant_center_ids?: any;
  status_ids?: Array<string>;
  checklist_status?: Array<string>;
  show_dismissed?: boolean;
  user_id?: string;
  is_case?: boolean;
  date_from?: string;
  date_to?: string;
  sort_field?: string;
  sort_type?: string;
  limit?: string;
  page?: number;
}

export const useSuperTeamDashboardStore = defineStore('super-team-dashboards', {
  state: (): {
    transplantCenters: null | Array<Lookup>,
    organs: null | Array<Lookup>,
    organGroups: null | Array<Lookup>,
    users: null | Array<Lookup>,
    statuses: null | Array<Lookup>,
    allTransplantCenters: null | Array<Lookup>,
    allOrganGroups: null | Array<Lookup>,
    allStatuses: null | Array<Lookup>,
    available_dashboards: null | Array<Lookup>,
    selectedDashboard?: SuperTeamDashboard,
    availableViews: Object,
    // nonRecoveries: null|Array<Lookup>,
    // finalDispositionCodes: null|Array<Lookup>,
    // matchServiceLevels: null|Array<Lookup>,
    // matchServiceLevelErrors: null|Array<Lookup>,
    filterParams: any,
    dashboardContent: {
      records: null | Array<SuperTeamDashboardRow>,
      totalRows: number | null
    },
    visibleContent: Array<SuperTeamDashboardRow>,
    failedRows: Array<SuperTeamDashboardRow>,
    dashboards: null // TODO: Check if this is used?
  } => {
    return {
      // lookup data
      transplantCenters: null,
      organs: null,
      organGroups: null,
      users: [],
      statuses: null,
      allTransplantCenters: null,
      allOrganGroups: null,
      allStatuses: null,
      available_dashboards: null,
      filterParams: {},
      availableViews: {},
      dashboardContent: {
        totalRows: null,
        records: []
      },
      visibleContent: [],
      failedRows: [],
      dashboards: null
    }
  },
  actions: {
    loadPaginatedEntity(url, key, params = {}) {
      return new Promise((resolve, reject) => {
        beApiClient.get(url, {
          headers: { 'Accept': 'application/json' },
          params: params
        })
          .then(response => {
            this[key] = response.data.data;
            resolve(response.data.data);
          }).catch(error => {
            const message = handleErrors(error);
            reject(message);
          });
      });
    },
    // load transplant centres
    loadAllTransplantCentres() {
      return this.loadPaginatedEntity(APIRoute(EP.admin.dashboards.internal_super_team.lookups.transplant_centers), 'allTransplantCenters', { limit: 500 })
    },
    // load organ groups
    loadAllOrganGroups() {
      return this.loadPaginatedEntity(APIRoute(EP.admin.dashboards.internal_super_team.lookups.organ_groups), 'allOrganGroups', { limit: 500 })
    },
    loadAllStatuses() {
      return this.loadPaginatedEntity(APIRoute(EP.admin.dashboards.internal_super_team.lookups.statuses), 'allStatuses', { limit: 500 })
    },

    loadSuperTeamDashboard(params: SuperTeamDashboardFilterParams = {}) {   
      return new Promise<void>(async (resolve, reject) => {
        try {
          const selectedDashboard = new SuperTeamDashboard();
          this.selectedDashboard = selectedDashboard;
          let urls = [
            EP.dashboards.internal_super_teams.lookups.transplant_centers,
            EP.dashboards.internal_super_teams.lookups.organ_groups,
            EP.dashboards.internal_super_teams.lookups.statuses,
            EP.dashboards.internal_super_teams.lookups.users_with_access,
            EP.dashboards.internal_super_teams.lookups.checklist_application_statuses
          ].map(url => beApiClient.get(APIRoute(url, { id: params.super_team_dashboard_configuration_id }), { headers: { 'Accept': 'application/json' } }))
          // Get the dropdowns
          await Promise.all(urls).then((responses: AxiosResponse) => {
            selectedDashboard.transplantCenters = responses[0].data.data;
            selectedDashboard.organGroups = responses[1].data.data;
            selectedDashboard.statuses = responses[2].data.data;
            selectedDashboard.assignableUsers = responses[3].data.data.map(item => ({
              id: item.id,
              full_name: item.full_name,
              name_to_display: item.full_name + ` (${item.email})`,
              disabled: item.deactivated_at ? true : false
            }));
            selectedDashboard.checklistStatuses = responses[4].data.data.map(status => ({
              ...status,
              label: STATUS_LABELS[status.code] || status.label
            }));
          });

          await this.loadSuperTeamDashboardRows(params);
          resolve();
        } catch(error) {
          reject(error);
        }
      });
    },

    loadSuperTeamDashboardRows(params: SuperTeamDashboardFilterParams) {
      return new Promise<void>(async (resolve, reject) => {
        const url = APIRoute(EP.dashboards.internal_super_teams.show, { id: params.super_team_dashboard_configuration_id });
        beApiClient.get(url, {
          headers: { 'Accept': 'application/json' },
          params: {
            match_code: params.match_code,
            donor_code: params.donor_code,
            organ_group_ids: params.organ_group_ids,
            transplant_center_ids: params.transplant_center_ids,
            status_ids: params.status_ids,
            checklist_status: params.checklist_status?.includes("") ? "" : params.checklist_status,
            user_id: params.user_id,
            show_dismissed: params.show_dismissed,
            is_case: params.is_case,
            date_from: params.date_from,
            date_to: params.date_to,
            sort_field: params.sort_field,
            sort_direction: params.sort_type,
            limit: params.limit,
            page: params.page
          }
        }).then((response: AxiosResponse) => {
          this.selectedDashboard.setContent({ 
            rows: response.data.data, 
            totalCount: response.data.pagy.count,
            totalPages: response.data.pagy.last,
            currentPage: response.data.pagy.page
          });
          resolve();
        }).catch((error: AxiosError) => {
          reject(error);
        });
      })
    },
    // Super Team Dashboard
    loadSuperTeamDashboards() {
      return this.loadPaginatedEntity(APIRoute(EP.dashboards.internal_super_teams.index), 'available_dashboards', { limit: 500 })
    },

    // Admin
    loadSuperTeamDashboardConfigs(params: any) {
      return new Promise<void>((resolve, reject) => {
        beApiClient.get(APIRoute(EP.admin.dashboards.internal_super_team.configurations.index), { params: params }).then((response: AxiosResponse) => {
          resolve(response.data);
        }).catch((error: AxiosError) => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
    loadSuperTeamDashboardConfig(id) {
      return makeRequest(beApiClient.get, APIRoute(EP.admin.dashboards.internal_super_team.configurations.show, { id: id }));
    },
    createSuperTeamDashboardConfig(payload) {
      return makeRequest(beApiClient.post, APIRoute(EP.admin.dashboards.internal_super_team.configurations.create), { internal_super_team_configuration: payload });
    },
    updateSuperTeamDashboardConfig(payload) {
      return makeRequest(beApiClient.patch, APIRoute(EP.admin.dashboards.internal_super_team.configurations.update, { id: payload.id }), { internal_super_team_configuration: payload });
    },
    saveView(payload, preferenceType, dashboardConfigId) {
      payload.dashboard_configuration_id = dashboardConfigId
      return new Promise<void>((resolve, reject) => {
        beApiClient.patch(APIRoute(EP.dashboards.internal_super_teams.saveView, { preference_type: encodeURIComponent(preferenceType) } ), payload).then(response => {
         
          resolve(response);
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
     loadViews(dashboardConfigId) {
      const payload = {
        dashboard_configuration_id: dashboardConfigId
      }
      return new Promise<void>((resolve, reject) => {
        beApiClient.get(APIRoute(EP.dashboards.internal_super_teams.loadViews ),{ params: payload} ).then(response => {
          const { dashboard_configuration_id, ...cleanPreferences } = response.data.preferences;
          this.availableViews = cleanPreferences;
          resolve();
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
    deleteView(preferenceType, dashboardConfigId) {
      return new Promise<void>((resolve, reject) => {
        beApiClient.delete(APIRoute(EP.dashboards.internal_super_teams.deleteView, { preference_type: encodeURIComponent(preferenceType) }), { params: { dashboard_configuration_id: dashboardConfigId } }).then(response => {
          resolve(response);
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
    renameView(preferenceType, newName, dashboardConfigId) {
      return new Promise<void>((resolve, reject) => {
        beApiClient.patch(APIRoute(EP.dashboards.internal_super_teams.renameView, { preference_type: encodeURIComponent(preferenceType) }), { new_name: newName, dashboard_configuration_id: dashboardConfigId }).then(response => {
          resolve(response);
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
  }
});